/* Humbler cookie banner (sections/humbler-cookie-banner.liquid).
   Native: Shopify's Customer Privacy API. Shopify.loadFeatures loads 'consent-tracking-api'; the banner
   shows only when customerPrivacy.shouldShowBanner() is true (the store requires consent in the visitor's
   region and no choice is stored). Choices go through customerPrivacy.setTrackingConsent(). If the API is
   unavailable, nothing shows and nothing blocks the page.
   Preview (html[data-humbler-preview="true"]): a minimal local stand-in for customerPrivacy; the choice is
   kept in localStorage 'humbler:preview-consent' (remove it to see the banner again).

   Public:
     window.HumblerConsent.open({ preferences = true, trigger }) -> Promise<boolean>  (focus moves into the card)
     window.HumblerConsent.close()
     window.HumblerConsent.getConsent() -> Promise<{ analytics, marketing, preferences, sale_of_data } | null>  ('yes' | 'no' | '')
   Re-open link without code: <a href="#cookie-preferences"> (e.g. a footer menu item) or any [data-hb-consent-open].
   Event after a saved choice: document 'humbler:consent'
     detail { analytics, marketing, preferences, sale_of_data, action: 'accept' | 'decline' | 'save' } */
(() => {
  if (window.HumblerConsent) return;

  const doc = document.documentElement;
  const isPreview = doc.dataset.humblerPreview === 'true';
  const PREVIEW_KEY = 'humbler:preview-consent';
  const KEYS = ['analytics', 'marketing', 'preferences', 'sale_of_data'];
  const OPEN_SELECTOR = 'a[href$="#cookie-preferences"], [data-hb-consent-open]';
  // Bottom bars the card must clear (fixed ones count while .is-visible; sticky ones while stuck to the bottom).
  const BARS = '.hb-pdp-sticky, .hb-lp-sticky, .hb-cart-page__bar, .hb-builder__summary, [data-hb-bottom-bar]';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = () => !!window.Shopify?.designMode;

  let apiPromise = null;
  let root = null;
  let returnFocus = null;
  let dismissible = false;
  let saving = false;
  let hideToken = 0;

  /* ---------- Customer Privacy API ---------- */
  function previewApi() {
    const read = () => { try { return JSON.parse(localStorage.getItem(PREVIEW_KEY) || 'null'); } catch { return null; } };
    return {
      humblerPreview: true,
      shouldShowBanner: () => !read(),
      currentVisitorConsent() {
        const stored = read();
        return Object.fromEntries(KEYS.map(key => [key, stored ? (stored[key] ? 'yes' : 'no') : '']));
      },
      setTrackingConsent(consent, callback) {
        try { localStorage.setItem(PREVIEW_KEY, JSON.stringify({ ...consent, saved_at: new Date().toISOString() })); }
        catch { /* storage blocked: the choice lasts for this page only */ }
        setTimeout(() => callback?.({}), 120);
      }
    };
  }

  const usable = api => api && typeof api.shouldShowBanner === 'function' && typeof api.setTrackingConsent === 'function' ? api : null;

  function loadApi() {
    if (apiPromise) return apiPromise;
    apiPromise = new Promise(resolve => {
      const shopify = window.Shopify;
      if (isPreview) {
        window.Shopify = shopify || {};
        if (!usable(window.Shopify.customerPrivacy)) window.Shopify.customerPrivacy = previewApi();
        resolve(usable(window.Shopify.customerPrivacy));
        return;
      }
      if (usable(shopify?.customerPrivacy)) { resolve(shopify.customerPrivacy); return; }
      if (typeof shopify?.loadFeatures !== 'function') { resolve(null); return; }
      const timer = setTimeout(() => resolve(null), 15000);
      try {
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
          clearTimeout(timer);
          resolve(error ? null : usable(window.Shopify?.customerPrivacy));
        });
      } catch {
        clearTimeout(timer);
        resolve(null);
      }
    });
    return apiPromise;
  }

  const safely = (fn, fallback) => { try { return fn(); } catch { return fallback; } };
  // Shopify shows its own banner where the store setting asks for it, which can include regions where
  // shouldShowBanner() is false. When Shopify injects that banner, this one takes its place (same API).
  const SHOPIFY_BANNER = '#shopify-pc__banner';
  const shopifyBannerShown = () => !!document.querySelector(SHOPIFY_BANNER);
  const undecided = api => {
    const current = safely(() => api.currentVisitorConsent(), null) || {};
    return ['analytics', 'marketing', 'preferences'].some(key => current[key] === '' || current[key] === undefined);
  };
  const needsChoice = api => !!api && (safely(() => api.shouldShowBanner(), false) === true || (shopifyBannerShown() && undecided(api)));
  // Hides Shopify's banner (CSS in humbler-base.css) only once this banner can take over.
  const takeOver = () => document.documentElement.classList.add('hb-consent-takeover');
  // humbler-base.css holds Shopify's banner back from the first paint while this one is on the page; when this one
  // cannot show (no consent API, or its stylesheet failed to load), Shopify's is let through again.
  const fallBack = () => document.documentElement.classList.add('hb-consent-fallback');

  /* ---------- stylesheet: fetched only when the banner is about to show ---------- */
  let cssPromise = null;
  function loadCss() {
    if (cssPromise) return cssPromise;
    const href = root?.dataset.css;
    if (!href || document.querySelector('link[data-hb-consent-css]')?.sheet) return (cssPromise = Promise.resolve(true));
    cssPromise = new Promise(resolve => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.dataset.hbConsentCss = '';
      link.addEventListener('load', () => resolve(true), { once: true });
      link.addEventListener('error', () => { cssPromise = null; link.remove(); resolve(false); }, { once: true });
      document.head.append(link);
    });
    return cssPromise;
  }

  /* ---------- elements ---------- */
  const $ = selector => root?.querySelector(selector);
  const inputs = () => [...(root?.querySelectorAll('[data-hb-consent-option]') || [])];
  const isOpen = () => !!root && !root.hidden;

  function prefill(api) {
    const current = (api && safely(() => api.currentVisitorConsent(), null)) || {};
    // Undecided ('') stays off: nothing is pre-ticked.
    inputs().forEach(input => { input.checked = current[input.dataset.hbConsentOption] === 'yes'; });
  }

  function setExpanded(expanded) {
    const prefs = $('[data-hb-consent-prefs]');
    const toggle = $('[data-hb-consent-toggle]');
    if (!prefs || !toggle) return false;
    prefs.hidden = !expanded;
    toggle.setAttribute('aria-expanded', String(expanded));
    root.classList.toggle('is-expanded', expanded);
    return true;
  }

  function setError(message) {
    const error = $('[data-hb-consent-error]');
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }

  function setBusy(button) {
    saving = !!button;
    root.classList.toggle('is-busy', saving);
    if (saving) root.setAttribute('aria-busy', 'true');
    else root.removeAttribute('aria-busy');
    root.querySelectorAll('.hb-button').forEach(item => item.classList.toggle('is-loading', item === button));
  }

  /* ---------- keep clear of floating add-to-bag bars ---------- */
  let liftFrame = 0;
  let barObserver = null;
  function measureLift() {
    const viewport = window.innerHeight;
    let lift = 0;
    document.querySelectorAll(BARS).forEach(bar => {
      if (!bar.offsetHeight || bar.hidden) return;
      const style = getComputedStyle(bar);
      if (style.display === 'none') return;
      if (style.position === 'fixed') {
        if (bar.classList.contains('is-visible')) lift = Math.max(lift, bar.offsetHeight);
        return;
      }
      const rect = bar.getBoundingClientRect();
      if (rect.bottom >= viewport - 1 && rect.top < viewport) lift = Math.max(lift, viewport - rect.top);
    });
    return Math.round(lift);
  }
  function updateLift() {
    liftFrame = 0;
    if (isOpen()) root.style.setProperty('--hb-consent-lift', `${measureLift()}px`);
  }
  const queueLift = () => { if (!liftFrame) liftFrame = requestAnimationFrame(updateLift); };
  function watchBars(on) {
    if (on) {
      updateLift();
      window.addEventListener('scroll', queueLift, { passive: true });
      window.addEventListener('resize', queueLift);
      if (!barObserver && 'MutationObserver' in window) {
        barObserver = new MutationObserver(queueLift);
        document.querySelectorAll(BARS).forEach(bar => barObserver.observe(bar, { attributes: true, attributeFilter: ['class', 'hidden', 'style'] }));
        barObserver.observe(doc, { attributes: true, attributeFilter: ['class'] });
      }
    } else {
      window.removeEventListener('scroll', queueLift);
      window.removeEventListener('resize', queueLift);
      barObserver?.disconnect();
      barObserver = null;
    }
  }

  /* ---------- show / hide ---------- */
  async function show({ expand = false, focus = false, force = false } = {}) {
    if (!root) return false;
    const api = await loadApi();
    if (!api && !force) return false;
    if (!(await loadCss())) { fallBack(); return false; }
    if (!root?.isConnected) return false;
    const wasOpen = isOpen();
    if (!wasOpen) prefill(api);
    // A shopper who re-opens it after choosing (or where no consent is required) can close it again.
    dismissible = !needsChoice(api);
    if (!dismissible) takeOver();
    const close = $('[data-hb-consent-close]');
    if (close) close.hidden = !dismissible;
    setExpanded(expand);
    setError('');
    hideToken++; // cancels a hide that is still animating out
    root.classList.remove('is-leaving');
    root.hidden = false;
    watchBars(true);
    if (focus) {
      const target = (expand && inputs()[0]) || $('[data-hb-consent-accept]');
      target?.focus({ preventScroll: true });
    }
    return true;
  }

  function hide({ restoreFocus = true } = {}) {
    if (!isOpen()) return;
    const hadFocus = root.contains(document.activeElement);
    const token = ++hideToken;
    const finish = () => {
      if (token !== hideToken) return;
      root.classList.remove('is-leaving');
      root.hidden = true;
      setExpanded(false);
      watchBars(false);
    };
    if (reduceMotion.matches || !root.getAnimations) finish();
    else {
      root.classList.add('is-leaving');
      const animations = root.getAnimations();
      if (animations.length) {
        Promise.all(animations.map(animation => animation.finished)).then(finish, finish);
        setTimeout(finish, 400); // never wait on a paused timeline (background tab)
      } else finish();
    }
    if (restoreFocus && hadFocus && returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
    returnFocus = null;
  }

  /* ---------- choices ---------- */
  function consentFor(action) {
    if (action === 'accept' || action === 'decline') {
      const value = action === 'accept';
      return { analytics: value, marketing: value, preferences: value, sale_of_data: value };
    }
    const chosen = Object.fromEntries(inputs().map(input => [input.dataset.hbConsentOption, input.checked]));
    const marketing = !!chosen.marketing;
    // Sale/sharing of data follows the marketing choice.
    return { analytics: !!chosen.analytics, marketing, preferences: !!chosen.preferences, sale_of_data: marketing };
  }

  async function choose(action, button) {
    if (saving) return;
    const consent = consentFor(action);
    const api = await loadApi();
    if (!api) { hide(); return; } // theme editor without the API: just close
    setError('');
    setBusy(button);
    let settled = false;
    const done = response => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      setBusy(null);
      if (response && response.error) {
        setError($('[data-hb-consent-error]')?.dataset.message || '');
        return;
      }
      hide();
      document.dispatchEvent(new CustomEvent('humbler:consent', { detail: { ...consent, action } }));
    };
    const timer = setTimeout(() => done({ error: 'timeout' }), 10000);
    try { api.setTrackingConsent(consent, done); }
    catch (error) { done({ error }); }
  }

  /* ---------- wiring ---------- */
  function bind(element) {
    root = element;
    if (root.dataset.hbBound) return;
    root.dataset.hbBound = 'true';

    root.addEventListener('click', event => {
      const button = event.target.closest('button');
      if (!button || !root.contains(button)) return;
      if (button.matches('[data-hb-consent-accept]')) choose('accept', button);
      else if (button.matches('[data-hb-consent-decline]')) choose('decline', button);
      else if (button.matches('[data-hb-consent-save]')) choose('save', button);
      else if (button.matches('[data-hb-consent-close]')) hide();
      else if (button.matches('[data-hb-consent-toggle]')) {
        const expand = button.getAttribute('aria-expanded') !== 'true';
        setExpanded(expand);
        queueLift();
        if (expand) root.scrollTop = 0;
      }
    });

    root.addEventListener('keydown', event => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const toggle = $('[data-hb-consent-toggle]');
      if (toggle?.getAttribute('aria-expanded') === 'true') {
        event.preventDefault();
        setExpanded(false);
        toggle.focus();
      } else if (dismissible) {
        event.preventDefault();
        hide();
      }
    });
  }

  async function open({ preferences = true, trigger = null } = {}) {
    if (!root) init();
    if (!root) return false;
    const active = trigger || document.activeElement;
    returnFocus = active && active !== document.body && !root.contains(active) ? active : returnFocus;
    const hasPrefs = !!$('[data-hb-consent-prefs]');
    return show({ expand: preferences && hasPrefs, focus: true, force: designMode() });
  }

  function init() {
    const element = document.querySelector('[data-hb-consent]');
    if (!element) { root = null; return; }
    bind(element);
    loadApi().then(api => {
      if (root !== element) return;
      if (!api) { fallBack(); return; }
      if (needsChoice(api)) { show(); return; }
      if (isPreview || bannerObserver) return;
      bannerObserver = new MutationObserver(() => {
        if (!shopifyBannerShown()) return;
        bannerObserver.disconnect();
        if (root === element && needsChoice(api)) show();
      });
      bannerObserver.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => bannerObserver?.disconnect(), 20000);
    });
  }
  let bannerObserver = null;

  document.addEventListener('click', event => {
    const trigger = event.target.closest?.(OPEN_SELECTOR);
    if (!trigger || root?.contains(trigger)) return;
    event.preventDefault();
    open({ trigger });
  });

  // A choice stored elsewhere (another banner, another tab's script) closes a banner the visitor didn't open.
  document.addEventListener('visitorConsentCollected', () => {
    if (isOpen() && !saving && !dismissible) loadApi().then(api => { if (!needsChoice(api)) hide({ restoreFocus: false }); });
  });

  // Theme editor: show while the section is selected, re-bind after it re-renders.
  document.addEventListener('shopify:section:load', event => {
    if (!event.target.querySelector?.('[data-hb-consent]')) return;
    init();
    if (designMode()) show({ force: true });
  });
  document.addEventListener('shopify:section:select', event => {
    if (event.target.querySelector?.('[data-hb-consent]')) show({ force: true });
  });
  document.addEventListener('shopify:section:deselect', event => {
    if (!event.target.querySelector?.('[data-hb-consent]')) return;
    loadApi().then(api => { if (!needsChoice(api)) hide({ restoreFocus: false }); });
  });

  window.HumblerConsent = {
    open,
    close: () => hide(),
    getConsent: () => loadApi().then(api => (api ? safely(() => api.currentVisitorConsent(), null) : null))
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  if (location.hash === '#cookie-preferences') loadApi().then(api => { if (api) open(); });
})();
